// ResetPasswordPage.tsx — step 2 of password reset: set the new password.
//
// Token-carrying route, same shape as VerifyEmailPage (useSearchParams → token, a
// small state machine, no session ever created here).
//
// Completing this does NOT sign the member in, deliberately. Three paths on this
// product turn proof-of-credential into a session and each honours 2FA by
// convention; a reset that logged you in would be a fourth and the strongest, and
// would reduce a 2FA-protected account's security to its mailbox. So this sets the
// password and sends them to /signin, where the 2FA gate applies as it always does.
//
// And for an account WITH 2FA, a code is required before the password changes at
// all — the server answers `totp_required`, and this form then reveals the code
// field and resubmits.

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Panel, TextField, Button, useToast } from '@/ui';
import { authService, RateLimited, TotpRequired } from '@/core/services/authService';

const Wrap = styled.div`
  min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 32px;
`;
const Card = styled(Panel).attrs({ $brackets: false })`
  max-width: 440px; width: 100%; display: flex; flex-direction: column; gap: 14px;
`;
const Title = styled.h1`
  margin: 0; font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg}; color: ${({ theme }) => theme.color.textPrimary};
`;
const Body = styled.p`
  margin: 0; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.55;
`;
const ErrorText = styled.p`
  margin: 0; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.danger};
`;
const BackLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.accentCyan};
  text-decoration: none; align-self: center;
`;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const toast = useToast();

  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <Wrap>
        <Card>
          <Title>Link incomplete</Title>
          <Body>
            This page needs the link from your email. Open that link directly, or ask for a
            new one.
          </Body>
          <BackLink to="/forgot-password">Request a new link</BackLink>
        </Card>
      </Wrap>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (next.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (next !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (needsCode && !code.trim()) {
      setError('Enter the code from your authenticator app, or one of your recovery codes.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await authService.confirmPasswordReset(token!, next, code.trim() || undefined);
      toast('Password updated. Sign in with your new password.');
      void navigate('/signin', { replace: true });
    } catch (err) {
      if (err instanceof TotpRequired) {
        // Not a mistake the member made: their account has 2FA, so the form grows a
        // code field and they resubmit. The password they typed is kept.
        setNeedsCode(true);
        setError('');
      } else if (err instanceof RateLimited) {
        setError('Too many attempts. Please wait a while before trying again.');
      } else if (isCode(err, 'invalid_code')) {
        setError('That code was not right. Try again, or use one of your recovery codes.');
      } else if (isCode(err, 'validation')) {
        setError('That password is too easy to guess. Try a longer or less common one.');
      } else {
        setError(
          'This link is invalid, expired, or already used. Request a fresh one and try again.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Wrap>
      <Card as="form" onSubmit={(e: React.FormEvent) => void handleSubmit(e)}>
        <Title>Set a new password</Title>
        {needsCode ? (
          <Body>
            This account uses two-factor authentication. Enter a code from your authenticator
            app, or one of your recovery codes, to finish.
          </Body>
        ) : (
          <Body>Choose a new password for your account.</Body>
        )}
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => { setNext(e.currentTarget.value); setError(''); }}
        />
        <TextField
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => { setConfirm(e.currentTarget.value); setError(''); }}
        />
        {needsCode && (
          <TextField
            label="Authentication code"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => { setCode(e.currentTarget.value); setError(''); }}
          />
        )}
        {error && <ErrorText role="alert">{error}</ErrorText>}
        <Button type="submit" variant="primary">
          {busy ? 'Saving…' : 'Set new password'}
        </Button>
        <BackLink to="/signin">Back to sign in</BackLink>
      </Card>
    </Wrap>
  );
}

/** Narrow an unknown thrown value to an ApiError-shaped object carrying `code`. */
function isCode(err: unknown, code: string): boolean {
  return typeof err === 'object' && err !== null && (err as { code?: string }).code === code;
}
